// TutorUserProfile.jsx (Tailwind rewrite)
//  Remove: import "../styles/TutorUserProfile.css";

import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import Footer from "../Footer";

const API_BASE = "https://tn.server.goatteachers.com/api";

const StatCard = ({ value, label, icon }) => (
  <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
    <div className="text-2xl font-extrabold text-zinc-900">{value}</div>
    <div className="mt-1 text-sm font-semibold text-zinc-600">
      <span className="mr-1">{icon}</span>
      {label}
    </div>
  </div>
);

const Pill = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-xs font-semibold text-zinc-700">
    {children}
  </span>
);

const Field = ({ label, value }) => (
  <div className="rounded-xl border border-zinc-200 bg-white p-3">
    <div className="text-xs font-semibold text-zinc-500">{label}</div>
    <div className="mt-1 text-sm font-semibold text-zinc-900">
      {value || "-"}
    </div>
  </div>
);

const formatSalary = (c) => {
  const min = c?.minSalary;
  const max = c?.maxSalary;
  if (min && max) return `${min}-${max} ৳`;
  if (min) return `${min}+ ৳`;
  if (max) return `up to ${max} ৳`;
  return "";
};

const SafeDate = ({ value }) => {
  const text = useMemo(() => {
    if (!value) return "";
    try {
      return new Date(value).toLocaleString();
    } catch {
      return "";
    }
  }, [value]);
  return <span>{text}</span>;
};

const TutorUserProfile = () => {
  const { user, logoutUser } = useContext(UserContext);
  const navigate = useNavigate();

  const [tutorData, setTutorData] = useState(null);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [connectionsList, setConnectionsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);

  const [revealedContacts, setRevealedContacts] = useState([]);
  const [revealedLoading, setRevealedLoading] = useState(true);

  const credits = Number(wallet?.credits || 0);
  const pending = Number(wallet?.pending_topups || 0);

  const tutorId = tutorData?.id || tutorData?._id;
  const tutorProfileUrl = tutorId
    ? `${window.location.origin}/tutor/${tutorId}`
    : "";

  const whatsappText = useMemo(() => {
    return `Assalamu Walaikum, i m interested in tuition job.

My CV:
Name: ${tutorData?.name || "-"}
Degree: ${tutorData?.degree || "-"}
Institute: ${tutorData?.institute || "-"}

Tuition Preference:
Medium: ${(tutorData?.mediums || []).join(", ") || "-"}
Subject(s): ${(tutorData?.subjects || []).join(", ") || "-"}
Class/Level: ${(tutorData?.levels || []).join(", ") || "-"}

My Tutor Profile: ${tutorProfileUrl || "-"}
`;
  }, [tutorData, tutorId, tutorProfileUrl]);

  const normalizeBDPhone = (raw) => {
    if (!raw) return "";
    let d = String(raw).replace(/\D/g, ""); // digits only

    if (d.startsWith("00")) d = d.slice(2);
    if (d.startsWith("880")) return d; // already correct
    if (d.startsWith("88")) return d; // 88 + local
    if (d.startsWith("0")) return "880" + d.slice(1); // 017.. -> 88017..
    if (d.startsWith("1")) return "880" + d; // 17.. -> 88017..
    return d;
  };

  const authHeaders = useMemo(() => {
    if (!user?.token) return null;
    return { Authorization: `Bearer ${user.token}` };
  }, [user?.token]);

  const fetchWallet = async () => {
    if (!authHeaders) return;
    try {
      setWalletLoading(true);
      const res = await axios.get(`${API_BASE}/tutors/wallet/me`, {
        headers: authHeaders,
      });
      setWallet(res.data || null);
    } catch (err) {
      console.error("Wallet fetch error:", err);
      setWallet(null);
    } finally {
      setWalletLoading(false);
    }
  };

  const fetchRevealedTuitionContacts = async () => {
    if (!authHeaders) return;
    try {
      setRevealedLoading(true);
      const res = await axios.get(
        `${API_BASE}/tutors/me/revealed-tuition-contacts`,
        {
          headers: authHeaders,
          params: { limit: 50 },
        },
      );
      const unique = Array.from(
        new Map(
          (res.data?.contacts || []).map((c) => [
            `${c.tuitionPostId}-${c.revealedAt}`,
            c,
          ]),
        ).values(),
      );

      setRevealedContacts(unique);
    } catch (err) {
      console.error("Revealed contacts fetch error:", err);
      setRevealedContacts([]);
    } finally {
      setRevealedLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (user.role !== "tutor") {
      navigate("/login");
      return;
    }

    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API_BASE}/tutors/me`, {
          headers: authHeaders,
        });

        const tutor = res.data?.tutor || null;
        setTutorData(tutor);

        const tutorId = tutor?.id;
        if (tutorId) {
          const [summaryRes, connectionsRes] = await Promise.all([
            axios.get(`${API_BASE}/tutors/${tutorId}/analytics/summary`, {
              headers: authHeaders,
            }),
            axios.get(`${API_BASE}/tutors/${tutorId}/analytics/connections`, {
              headers: authHeaders,
              params: { limit: 20 },
            }),
          ]);
          setAnalyticsSummary(summaryRes.data || null);
          setConnectionsList(connectionsRes.data?.connections || []);
        }

        await Promise.all([fetchWallet(), fetchRevealedTuitionContacts()]);
      } catch (err) {
        console.error("Profile fetch error:", err);
        if ([401, 403].includes(err?.response?.status)) {
          logoutUser();
          navigate("/login");
        }
      } finally {
        setLoading(false);
        setLoadingAnalytics(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, logoutUser, authHeaders]);

  const totalViews =
    analyticsSummary?.totals?.profile_views ??
    tutorData?.profile_views_count ??
    0;

  const totalConnections =
    analyticsSummary?.totals?.connections ?? tutorData?.connections_count ?? 0;

  const avatarUrl =
    tutorData?.profile_picture ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(
      tutorData?.name || "Tutor",
    )}&background=111827&color=fff`;

  if (loading) {
    return (
      <>
        <div className="min-h-[60vh] bg-zinc-50">
          <div className="mx-auto max-w-6xl px-4 py-10">
            <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
              <div className="animate-pulse space-y-4">
                <div className="h-5 w-40 rounded bg-zinc-200" />
                <div className="h-10 w-full rounded bg-zinc-200" />
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="h-20 rounded-xl bg-zinc-200" />
                  <div className="h-20 rounded-xl bg-zinc-200" />
                  <div className="h-20 rounded-xl bg-zinc-200" />
                  <div className="h-20 rounded-xl bg-zinc-200" />
                </div>
              </div>
              <div className="mt-6 text-sm font-semibold text-zinc-600">
                Loading your dashboard…
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-zinc-50">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:py-10">
          {/* Header */}
          <div className="rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm sm:p-7">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-4 rounded-3xl bg-white p-5 ring-1 ring-zinc-200 shadow-sm sm:flex-row sm:items-center sm:gap-5 sm:p-6">
                <div className="shrink-0">
                  <img
                    className="h-20 w-20 rounded-3xl object-cover ring-2 ring-zinc-200 sm:h-24 sm:w-24"
                    src={avatarUrl}
                    alt="Tutor Avatar"
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="text-2xl font-extrabold tracking-tight text-zinc-950 sm:text-3xl">
                    {tutorData?.name || "-"}
                  </div>

                  <div className="mt-1 break-words text-sm font-medium text-zinc-500 sm:text-base">
                    {tutorData?.email || "-"}
                  </div>

                  <div className="mt-4 flex flex-col gap-1.5 text-sm text-slate-600 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
                    <span className="font-semibold text-slate-700">
                      Role: Tutor
                    </span>

                    {tutorData?.institute ? (
                      <>
                        <span className="hidden text-slate-300 sm:inline">
                          •
                        </span>
                        <span className="leading-relaxed">
                          <span className="font-semibold text-slate-700">
                            University: {tutorData.institute}
                          </span>
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  onClick={() => navigate("/packages")}
                  className="inline-flex items-center justify-center rounded-2xl bg-[#25d976] px-4 py-2.5 text-sm font-extrabold text-black shadow-sm transition hover:bg-[#20c96b] active:scale-[0.99]"
                >
                  Become Tutor Pro
                </button>
              </div>
            </div>

            {/* Quick stats */}
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-4">
              <StatCard
                value={walletLoading ? "…" : credits}
                label="Applications Left"
              />

              <StatCard
                value={
                  walletLoading
                    ? "…"
                    : credits > 0
                      ? "Package Active"
                      : pending > 0
                        ? "Payment Confirmed — Package will be active soon"
                        : "No Package Active"
                }
                label=" Package Status"
                helper={
                  walletLoading
                    ? "…"
                    : "Purchased packages waiting for admin approval"
                }
              />
              <StatCard
                value={totalViews}
                label="Students Viewed Your Profile"
              />
              <StatCard value={totalConnections} label="Students Reached Out" />
            </div>

            {/* Analytics mini cards 
            {!loadingAnalytics && analyticsSummary ? (
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
                  <div className="text-xs font-extrabold uppercase tracking-wide text-zinc-500">
                    Last 7 Days
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Pill>Views: {analyticsSummary?.last7d?.views ?? 0}</Pill>
                    <Pill>
                      Connections: {analyticsSummary?.last7d?.connections ?? 0}
                    </Pill>
                  </div>
                </div>
                <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
                  <div className="text-xs font-extrabold uppercase tracking-wide text-zinc-500">
                    Last 30 Days
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Pill>
                      Views: {analyticsSummary?.last30d?.views ?? 0}
                    </Pill>
                    <Pill>
                      Connections:{" "}
                      {analyticsSummary?.last30d?.connections ?? 0}
                    </Pill>
                  </div>
                </div>
              </div>
            ) : null} */}
          </div>

          {/* Two-column layout */}
          <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.15fr]">
            {/* Left: Tutor details */}
            <div className="space-y-6">
              <div className="rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm sm:p-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-lg font-extrabold text-zinc-900">
                    Your Details
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-zinc-200 bg-zinc-50 p-4">
                  <div className="text-xs font-extrabold uppercase tracking-wide text-zinc-500">
                    Bio
                  </div>
                  <div className="mt-2 text-sm font-semibold leading-relaxed text-zinc-800">
                    {tutorData?.bio || "No bio added yet."}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Phone" value={tutorData?.connect?.phone} />
                  <Field
                    label="WhatsApp"
                    value={tutorData?.connect?.whatsapp}
                  />
                  <Field label="Gender" value={tutorData?.gender} />
                  <Field label="Degree" value={tutorData?.degree} />
                  <Field
                    label="Field of Study"
                    value={tutorData?.field_of_study}
                  />
                  <Field label="Institute" value={tutorData?.institute} />
                  <Field
                    label="Subjects"
                    value={
                      tutorData?.subjects?.length
                        ? tutorData.subjects.join(", ")
                        : "-"
                    }
                  />
                  <Field
                    label="Mediums"
                    value={
                      tutorData?.mediums?.length
                        ? tutorData.mediums.join(", ")
                        : "-"
                    }
                  />
                  <Field
                    label="Levels"
                    value={
                      tutorData?.levels?.length
                        ? tutorData.levels.join(", ")
                        : "-"
                    }
                  />
                  <Field
                    label="Teaching Mode"
                    value={tutorData?.teaching_mode}
                  />
                </div>
                <div className="mt-10 ">
                  <Link
                    to="/tutor/me/edit"
                    onClick={() => window.scrollTo(0, 0)}
                    className="inline-flex items-center justify-center rounded-2xl bg-zinc-900 px-4 py-2.5 text-sm font-extrabold text-white shadow-sm transition hover:bg-black active:scale-[0.99]"
                  >
                    Update Profile
                  </Link>
                </div>
              </div>

              {/* Recent Connections */}
              <div className="rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm sm:p-6">
                <div className="text-lg font-extrabold text-zinc-900">
                  Students who applied
                </div>

                {connectionsList.length === 0 ? (
                  <div className="mt-3 text-sm font-semibold text-zinc-600">
                    No student applied to you yet — keep teaching to level up!
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    {connectionsList.map((c, index) => (
                      <div
                        key={`${c.studentId}-${c.revealedAt}-${c.method}-${index}`}
                        className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-extrabold text-zinc-900">
                              #{index + 1}{" "}
                              <span className="ml-1 font-semibold text-zinc-700">
                                {c.studentName || "Anonymous Student"}
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <Pill>
                                {c.method === "phone"
                                  ? "📞 Via Phone"
                                  : "💬 Via WhatsApp"}
                              </Pill>
                              <span className="text-xs font-semibold text-zinc-500">
                                <SafeDate value={c.revealedAt} />
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Unlocked tuition contacts */}
            <div className="rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-lg font-extrabold text-zinc-900">
                    Tuitions you applied
                  </div>
                  <div className="mt-1 text-sm font-semibold text-zinc-600">
                    Quick access to tuitions you applied.
                  </div>
                </div>

                <button
                  onClick={fetchRevealedTuitionContacts}
                  className="inline-flex items-center justify-center rounded-2xl bg-zinc-900 px-4 py-2.5 text-sm font-extrabold text-white shadow-sm transition hover:bg-black active:scale-[0.99]"
                >
                  Refresh
                </button>
              </div>

              {revealedLoading ? (
                <div className="mt-4 text-sm font-semibold text-zinc-600">
                  Loading revealed contacts…
                </div>
              ) : revealedContacts.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-zinc-200 bg-zinc-50 p-4 text-sm font-semibold text-zinc-700">
                  You haven’t unlocked any tuition contacts yet.
                </div>
              ) : (
                <div className="mt-5 grid gap-3">
                  {revealedContacts.map((c, idx) => {
                    const salary = formatSalary(c);
                    const waPhone = normalizeBDPhone(c.whatsapp);

                    return (
                      <div
                        key={`${c.tuitionPostId}-${c.revealedAt}-${idx}`}
                        className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm"
                      >
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <div className="text-sm font-extrabold text-zinc-900">
                              {c.studentName || "Student"}{" "}
                              <span className="font-semibold text-zinc-600">
                                ({c.posterType || "Student"})
                              </span>
                            </div>

                            <div className="mt-2 flex flex-wrap gap-2">
                              {salary ? <Pill>Salary: {salary}</Pill> : null}
                              {c.status ? (
                                <Pill>Status: {c.status}</Pill>
                              ) : null}
                              {c.tuitionPostId ? (
                                <Pill>Post #{c.tuitionPostId}</Pill>
                              ) : null}
                            </div>
                          </div>

                          <div className="text-xs font-semibold text-zinc-500 sm:text-right">
                            <SafeDate value={c.revealedAt} />
                          </div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          {/* {c.phone ? (
                            <a
                              href={`tel:${c.phone}`}
                              className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-extrabold text-zinc-900 shadow-sm transition hover:bg-zinc-50"
                            >
                              <span>📞</span>
                              {c.phone}
                            </a>
                          ) : (
                            <span className="text-sm font-semibold text-zinc-500">
                              No phone
                            </span>
                          )} */}

                          {c.whatsapp ? (
                            waPhone ? (
                              <a
                                href={`https://api.whatsapp.com/send?phone=${waPhone}&text=${encodeURIComponent(
                                  whatsappText,
                                )}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-extrabold text-zinc-900 shadow-sm transition hover:bg-zinc-50"
                              >
                                ✉︎ WhatsApp
                              </a>
                            ) : (
                              <span className="text-sm font-semibold text-zinc-500">
                                Invalid WhatsApp
                              </span>
                            )
                          ) : (
                            <span className="text-sm font-semibold text-zinc-500">
                              No WhatsApp
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default TutorUserProfile;
